'use client';

import { ReactNode, useEffect } from 'react';
import { AbstractIntlMessages, NextIntlClientProvider } from 'next-intl';

import { ThemeProvider } from '@northladder/design-system';
import { LocaleProvider } from '@northladder/i18n';
import {
  ReactQueryProvider,
  TCompanyId,
  useAuthStore,
  useDeviceInfo,
} from '@northladder/services';
import { TLocale } from '@northladder/utilities';
import { useInitSentry } from '@northladder/web-utils';

import { AppErrorBoundary } from '../../features/error-boundaries';

import { TenantConfigProvider } from './TenantConfigProvider';

interface IAppProvidersProps {
  children: ReactNode;
  companyId: TCompanyId;
  i18nMessages: AbstractIntlMessages;
  locale: TLocale;
}

/**
 * -----------------------------------------------------------------------------
 * The Root Layout shared by all parts of the application.
 */
export function AppProviders({
  companyId,
  children,
  i18nMessages,
  locale,
}: Readonly<IAppProvidersProps>) {
  useInitSentry();
  const { activityLog, updateActivityLog } = useAuthStore();

  const deviceInfo = useDeviceInfo({
    locale,
    channel: 'COSMETIC_DIAGNOSTICS_WEB_APP',
  });

  useEffect(() => {
    if (companyId && companyId !== activityLog?.companyId) {
      updateActivityLog({ companyId, deviceInfo });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityLog?.companyId]);

  // The time zone can either be statically defined, read from the
  // user profile if you store such a setting, or based on dynamic
  // request information like the locale or a cookie.
  // Defaulting to UAE time zone for now.
  const timeZone = 'Asia/Dubai';

  return (
    <ThemeProvider>
      <ReactQueryProvider>
        <NextIntlClientProvider
          locale={locale}
          messages={i18nMessages}
          timeZone={timeZone}
        >
          <AppErrorBoundary>
            <LocaleProvider isGlobalLocaleSwitcherVisible={false}>
              <TenantConfigProvider>{children}</TenantConfigProvider>
            </LocaleProvider>
          </AppErrorBoundary>
        </NextIntlClientProvider>
      </ReactQueryProvider>
    </ThemeProvider>
  );
}
